/**
 * Restore-a-version confirmation dialog (cloud_filesystem ADR-007).
 *
 * Restoring is FORWARD-ONLY: it creates a new version that brings back the
 * earlier state and keeps everything in between, so the consequence copy says
 * exactly that. The dialog defaults to a WHOLE-WORKSPACE restore and offers an
 * optional file picker to restore only selected paths.
 *
 * `onRestore` receives `undefined` for the whole-workspace default and the
 * chosen array for a file-level restore — the same optional `paths` the SDK's
 * `restoreVersion` takes. Selecting the file-level mode without choosing a file
 * DISABLES confirm rather than quietly restoring everything: a silent widening
 * of a destructive scope is exactly the fallback `no-fallbacks` bans.
 *
 * Never-git contract: see the header of
 * `VersionHistoryPanel.tsx` — no git vocabulary in any user-visible string.
 */

import { useEffect, useState } from 'react';
import { Button } from '@localagent/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@localagent/ui/components/dialog';
import type { VersionEntry } from '@localagent/sdk';

/** Verbatim consequence copy. */
export const RESTORE_VERSION_CONSEQUENCE =
  'Restore to this version? A new version will be created; the versions in between are kept.';

type RestoreScope = 'whole' | 'selected';

export interface RestoreVersionDialogProps {
  open: boolean;
  /** The entry being restored. `null` while the dialog is closed. */
  entry: VersionEntry | null;
  /** Paths the optional file picker offers. Empty ⇒ no picker. */
  availablePaths?: string[];
  isRestoring?: boolean;
  onClose: () => void;
  /** `paths` undefined ⇒ whole workspace; otherwise exactly the chosen paths. */
  onRestore: (version: string, paths?: string[]) => void;
}

export function RestoreVersionDialog({
  open,
  entry,
  availablePaths = [],
  isRestoring = false,
  onClose,
  onRestore,
}: RestoreVersionDialogProps) {
  const [scope, setScope] = useState<RestoreScope>('whole');
  const [selected, setSelected] = useState<string[]>([]);

  // Each opening starts from the whole-workspace default.
  useEffect(() => {
    if (open) {
      setScope('whole');
      setSelected([]);
    }
  }, [open, entry?.version]);

  if (!entry) return null;

  const togglePath = (path: string) => {
    setSelected((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path],
    );
  };

  const confirmDisabled = isRestoring || (scope === 'selected' && selected.length === 0);

  const handleConfirm = () => {
    // Preserve the picker's listed order so the emitted paths are stable.
    const paths =
      scope === 'selected' ? availablePaths.filter((p) => selected.includes(p)) : undefined;
    onRestore(entry.version, paths);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="restore-version-dialog">
        <DialogHeader>
          <DialogTitle>Restore version</DialogTitle>
          <DialogDescription data-testid="restore-version-consequence">
            {RESTORE_VERSION_CONSEQUENCE}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded bg-muted p-3" data-testid="restore-version-target">
          <p className="text-sm font-medium">{entry.message}</p>
          <p className="mt-0.5 font-mono text-xs text-muted-foreground">
            {entry.version.slice(0, 12)}
          </p>
        </div>

        {availablePaths.length > 0 && (
          <div className="flex flex-col gap-2 py-1">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="restore-scope"
                checked={scope === 'whole'}
                onChange={() => setScope('whole')}
                data-testid="restore-scope-whole"
              />
              Restore the whole workspace
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="restore-scope"
                checked={scope === 'selected'}
                onChange={() => setScope('selected')}
                data-testid="restore-scope-selected"
              />
              Restore only selected files
            </label>

            {scope === 'selected' && (
              <div
                className="max-h-48 overflow-y-auto"
                data-testid="restore-version-file-picker"
              >
                {availablePaths.map((path) => (
                  <label
                    key={path}
                    className="flex cursor-pointer items-center gap-2 rounded px-3 py-1.5 text-sm hover:bg-accent/50"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(path)}
                      onChange={() => togglePath(path)}
                      data-testid={`restore-path-${path}`}
                    />
                    <span className="truncate font-mono text-xs">{path}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isRestoring}
            data-testid="restore-version-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={confirmDisabled}
            data-testid="restore-version-confirm"
          >
            {isRestoring ? 'Restoring…' : 'Restore'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
